import { FC, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';

import { selectEditableStoryId } from 'admin/reducers/story-editor/selectors';
import { selectUserInfo } from 'admin/reducers/user/selectors';
import { usePubnubFacade } from 'admin/hooks/pubnub/use-pubnub-facade';
import { STORIES_PAGE } from 'admin/constants/routes';
import { setModal } from 'admin/actions/set-modal';
import { useAdminSelector, useAdminDispatch } from 'admin/reducers';
import { MODAL } from 'admin/constants/common';
import { last } from 'lodash';
import Pubnub from 'pubnub';
import {
	isJoinMessageEvent,
	isJoinMessageRecent,
	isStoryActivelyOccupiedByOther,
	StoryCoEditingMessageType,
	type StoryJoinMessage,
} from 'admin/components/common/StoryCoEditingObserver/story-co-editing-pubnub-utils';

const CoEditingObserver: FC = () => {
	const dispatch = useAdminDispatch();
	const pubnub = usePubnubFacade();
	const { push: historyPush } = useHistory();
	const storyId = useAdminSelector(selectEditableStoryId);
	const { email, name = 'noname', id: userId } = useAdminSelector(selectUserInfo);
	const navigateToStories = useCallback(() => historyPush(STORIES_PAGE), [historyPush]);

	const joinedStoryIdRef = useRef<string | undefined>();

	useEffect(() => {
		const channelName = storyId;

		if (channelName === undefined || !pubnub || !userId || !email) {
			return undefined;
		}

		if (joinedStoryIdRef.current === channelName) {
			return undefined;
		}

		const sessionEmail = email;
		const sessionName = name;

		const publishLeave = () => {
			pubnub.publish({
				channel: channelName,
				message: {
					email: sessionEmail,
					name: sessionName,
					type: StoryCoEditingMessageType.leave,
				},
			});
		};

		const subscribeToStoryChannel = async () => {
			await pubnub.publish({
				channel: channelName,
				message: {
					email: sessionEmail,
					name: sessionName,
					type: StoryCoEditingMessageType.join,
				} as StoryJoinMessage['message'],
			});
			pubnub.subscribe(channelName);
			pubnub.addListeners({
				message(messageEvent: Pubnub.MessageEvent) {
					// force kick out
					if (
						isJoinMessageEvent(messageEvent) &&
						isJoinMessageRecent(messageEvent) &&
						messageEvent.message.email &&
						messageEvent.message.email !== sessionEmail
					) {
						navigateToStories();

						// Display the modal only after a location change to prevent automatic
						// closure by `ModalManager`
						setTimeout(() => {
							dispatch(
								setModal({
									id: MODAL.KICK_OUT_INFO_MODAL,
									data: {
										name: messageEvent.message.name,
										email: messageEvent.message.email,
									},
								})
							);
						}, 100);
					}
				},
			});
			joinedStoryIdRef.current = channelName;
		};

		const joinToStoryChannel = async () => {
			const occupants = await pubnub.getChannelOccupants(channelName);
			const hasOtherOccupant = occupants?.some(occupant => occupant.uuid !== userId);

			if (hasOtherOccupant) {
				const response = await pubnub.fetchMessaged({ channels: [channelName], count: 1 });
				const channelMsgEvents = response.channels[channelName];
				const lastMsgEvent = last(channelMsgEvents);

				if (isStoryActivelyOccupiedByOther(lastMsgEvent, occupants, pubnub.uuid)) {
					dispatch(
						setModal({
							id: MODAL.STORY_EDITORS_CONFLICT,
							data: {
								name: lastMsgEvent?.message?.name ?? 'noname',
								email: lastMsgEvent?.message?.email ?? 'noname',
								onOk: () => subscribeToStoryChannel(),
								onCancel: navigateToStories,
							},
						})
					);

					return;
				}
			}

			await subscribeToStoryChannel();
		};

		joinToStoryChannel();

		return () => {
			if (joinedStoryIdRef.current === channelName) {
				publishLeave();
				pubnub.removeListeners();
				pubnub.unsubscribe(channelName);
				joinedStoryIdRef.current = undefined;
			}
		};
	}, [pubnub, userId, storyId, email, name, dispatch, navigateToStories]);

	return null;
};

export default CoEditingObserver;
